import {
  faArrowDownToSquare,
  faArrowUpFromSquare,
  faFileCircleMinus,
  faFileCirclePlus,
  faFiles,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ButtonGroup, Toolbar } from "@progress/kendo-react-buttons";
import { Splitter } from "@progress/kendo-react-layout";
import {
  fieldFocusIndex,
  selectedSectionName,
  selectedFieldName as selectedTemplateFieldName,
  showColumnsConstructorWindow,
  templateData,
} from "../../Template/NewTemplateSignals";
import { ColumnsConstructorGrid } from "./ColumnsConstructorGrid";
import { ColumnsConstructorPropertyPane } from "./ColumnsConstructorPropertyPane";
import {
  columnsConstructorSessionData,
  columnsConstructorUniqueColumnNames,
  columnsConstructorValidationErrors,
  editedPropertyValues,
  gridPaneSize,
  maxColumnId,
  nestedPanes,
  selectedColumnId,
  selectedColumnIndex,
} from "./ColumnsConstructorSignals";

const handlePropertyChange = ({ propName, propValue }) => {
  if (fieldFocusIndex.value === null) {
    templateData.value = {
      ...templateData.value,
      sections: templateData.value.sections.map((section) => {
        if (section.sectionName === selectedSectionName.value) {
          return {
            ...section,
            properties: {
              ...section.properties,
              [propName]: propValue,
            },
          };
        }
        return section;
      }),
    };
  } else {
    templateData.value = {
      ...templateData.value,
      sections: templateData.value.sections.map((section) => {
        if (section.sectionName === selectedSectionName.value) {
          return {
            ...section,
            fields: section.fields.map((field) => {
              if (field.fieldName === selectedTemplateFieldName.value) {
                return {
                  ...field,
                  properties: {
                    ...field.properties,
                    [propName]: propValue,
                  },
                };
              }
              return field;
            }),
          };
        }
        return section;
      }),
    };
  }
};

export const ColumnsConstructor = ({ currentPropertyValues }) => {
  if (!currentPropertyValues) {
    return;
  }

  //#region UI Interactions
  const handleSubmit = () => {
    const updatedGridRows = columnsConstructorSessionData.value.map((row) => {
      if (editedPropertyValues.value[row.id]) {
        return {
          ...row,
          properties: {
            ...row.properties,
            ...editedPropertyValues.value[row.id],
          },
        };
      }
      return row;
    });

    editedPropertyValues.value = {};

    handlePropertyChange({ propName: "Columns", propValue: updatedGridRows });
    showColumnsConstructorWindow.value = !showColumnsConstructorWindow.value;
  };

  const handleNewColumn = () => {
    const newId = maxColumnId.value + 1;
    const newColumn = {
      id: newId,
      columnName: "",
      columnType: "",
      properties: {},
    };
    const updated = [...columnsConstructorSessionData.value, newColumn];
    columnsConstructorSessionData.value = updated;
    selectedColumnId.value = newId;
  };

  const handleDuplicateColumn = () => {
    const selectedColumn = columnsConstructorSessionData.value[selectedColumnIndex.value];
    const newId = maxColumnId.value + 1;
    const newColumn = {
      ...selectedColumn,
      id: newId,
    };
    const updated = [...columnsConstructorSessionData.value, newColumn];
    columnsConstructorSessionData.value = updated;
    selectedColumnId.value = newId;
  };

  const handleDeleteColumn = () => {
    const updated = columnsConstructorSessionData.value.filter(
      (row) => row.id !== selectedColumnId.value
    );
    columnsConstructorSessionData.value = updated;
    selectedColumnId.value = null;
  };

  const handleMoveUp = () => {
    const rows = [...columnsConstructorSessionData.value];
    const index = selectedColumnIndex.value;

    if (index > 0) {
      [rows[index - 1], rows[index]] = [rows[index], rows[index - 1]];
      columnsConstructorSessionData.value = rows;
      selectedColumnId.value = rows[index - 1].id;
    }
  };

  const handleMoveDown = () => {
    const rows = [...columnsConstructorSessionData.value];
    const index = selectedColumnIndex.value;

    if (index < rows.length - 1 && index >= 0) {
      [rows[index], rows[index + 1]] = [rows[index + 1], rows[index]];
      columnsConstructorSessionData.value = rows;
      selectedColumnId.value = rows[index + 1].id;
    }
  };
  //#endregion UI Interactions

  return (
    <div className="condition-rule-page-content">
      <div className="gallery-constructor-container">
        <Toolbar>
          <ButtonGroup>
            <Button onClick={handleNewColumn}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faFileCirclePlus}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                New Column
              </div>
            </Button>
            <Button onClick={() => handleDeleteColumn()}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  className="toolbar-button-icon"
                  icon={faFileCircleMinus}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                Delete Column
              </div>
            </Button>
            <Button onClick={handleDuplicateColumn}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faFiles}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                Duplicate Column
              </div>
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button onClick={handleMoveUp}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faArrowUpFromSquare}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
              </div>
            </Button>
            <Button onClick={handleMoveDown}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faArrowDownToSquare}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
              </div>
            </Button>
          </ButtonGroup>
          <div></div>
        </Toolbar>
        <Splitter
          style={{
            height: "90%",
            maxHeight: "90%",
            borderBottomRightRadius: "20px",
            borderBottomLeftRadius: "20px",
            overflowY: "auto",
          }}
          orientation="horizontal"
          panes={nestedPanes.value}
          onChange={(e) => (gridPaneSize.value = e.newState[0].size)}
        >
          <ColumnsConstructorGrid />
          <ColumnsConstructorPropertyPane />
        </Splitter>
      </div>
      <div className="expression-builder-buttons">
        <Button
          themeColor={"success"}
          onClick={() => handleSubmit()}
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
          disabled={
            columnsConstructorValidationErrors.value.length > 0 ||
            columnsConstructorUniqueColumnNames.value.length > 0
          }
        >
          Save
        </Button>
        <Button
          themeColor={"info"}
          onClick={() => {
            showColumnsConstructorWindow.value = !showColumnsConstructorWindow.value;
            columnsConstructorSessionData.value = [];
          }}
          className="k-ml-3 k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default ColumnsConstructor;
